import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, EventEmitter, Output } from "@angular/core"; import { Location } from "@angular/common";

import { lastValueFrom, map, Observable, of } from 'rxjs';

import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { ReponseBase } from './reponse-base';
import { Emplacement } from '../entites/emplacement';
import { ItemMin } from '../entites/item-min';
import { RapportEntite } from '../entites/rapport';
import { ParametresRapportItem, RequeteProduireRapportItem } from '../services/requetes/rapports/parametres-rapport-item';
import { ReponseProduireRapport } from './reponses/reponse-produire-rapport';
import { ParametresRapportUtilisationEquipements } from './requetes/rapports/parametres-rapport-utilisation-equipements';
import { ReponseRapportUtilisationEquipements } from './reponses/reponse-rapport-utilisation-equipements';
import { ReponseObtenirDatesRapportUtilisationEquipements } from './reponses/reponse-dates-rapport-utilisation-equipements';
import { ParametresRapportEntreesSorties } from './requetes/rapports/parametres-rapport-entrees-sorties';
import { ReponseRapportEntreesSorties } from './reponses/reponse-rapport-entrees-sorties';
import { RequeteProduireRapportExcel } from './requetes/requete-produire-excel';
import { ParametresRapportCommande } from './requetes/rapports/parametres-rapport-commande';
import { RequeteRapportHeuresLocation } from './requetes/requete-rapport-heures-location';
import { MessageService } from 'primeng/api';
import { NotificationService } from './notification-service';
import { construireUrlRapportPreCommande } from './rapport-precommande.helpers';

@Injectable({ providedIn: 'root' })
export class RapportService {

  private precommandeUrl = environment.apiBaseUrl + 'rapport/precommande/{id}';
  private commandeUrl = environment.apiBaseUrl + 'rapport/commande';
  private commandeParDepartementUrl = environment.apiBaseUrl + 'rapport/commande/departement';
  private bonCommandeUrl = environment.apiBaseUrl + 'rapport/bonCommande/{id}';
  private requisitionUrl = environment.apiBaseUrl + 'rapport/requisition/{id}';
  private itemUrl = environment.apiBaseUrl + 'rapport/item';
  private datesUtilisationEquipementUrl = environment.apiBaseUrl + 'rapport/datesutilisationequipements';
  private utilisationEquipementUrl = environment.apiBaseUrl + 'rapport/utilisationequipements';
  private entresSortiesUrl = environment.apiBaseUrl + 'rapport/entreessorties';
  private locationUrl = environment.apiBaseUrl + 'rapport/location';
  private locationIsoUrl = environment.apiBaseUrl + 'rapport/locationiso';
  private heureslocationUrl = environment.apiBaseUrl + 'rapport/heureslocation';
  private listeEquipementUrl = environment.apiBaseUrl + 'rapport/listeequipement';
  private boiteOutilUrl = environment.apiBaseUrl + 'rapport/boiteoutil/{id}';
  private bonTravailUrl = environment.apiBaseUrl + 'rapport/bontravail/{id}?multiPages={multiPages}&soumission={soumission}&pageVierge={pageVierge}';
  private analyseBonTravailUrl = environment.apiBaseUrl + 'rapport/analysebontravail/{id}';
  private soumissionUrl = environment.apiBaseUrl + 'rapport/soumission/{id}';
  private entretienPEPUrl = environment.apiBaseUrl + 'rapport/entretienpep/{dateDebut}/{dateFin}';
  private entretienPreventifUrl = environment.apiBaseUrl + 'rapport/entretienpreventif/{itemId}';
  private entretienSAAQUrl = environment.apiBaseUrl + 'rapport/entretiensaaq/{dateDebut}/{dateFin}';
  private compilationNonConformiteUrl = environment.apiBaseUrl + 'rapport/compilationnonconformite';
  private fichesSignaletiquesUrl = environment.apiBaseUrl + 'rapport/fichessignaletiques/{emplacementId}';
  private fichesSignaletiquesPdfUrl = environment.apiBaseUrl + 'rapport/fichessignaletiquespdf/{emplacementId}';  private saisieHeuresUrl = environment.apiBaseUrl + 'rapport/saisieheures';
  private exportationExcelUrl = environment.apiBaseUrl + 'rapport/excel';
  private sstUrl = environment.apiBaseUrl + 'rapport/sst/{id}';
  private nonConformiteUrl = environment.apiBaseUrl + 'rapport/nonconformite/{id}';

  public httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private http: HttpClient,
    public router: Router,
    public messageService: MessageService,
    private notificationService: NotificationService
  ) { }

  /** Builds the browser URL to view a generated rapport PDF. */
  public buildViewUrl(chemin: string, nom?: string): string {
    let url = `${environment.viewRapportsBaseUrl}v/${chemin}`;
    if (nom) {
      url += `?nom=${encodeURIComponent(nom)}`;
    }
    return url;
  }

  /** Prefers chemin-based view URLs so Docker-internal notification links still open correctly. */
  public resolveNotificationViewUrl(donnees?: { url?: string; chemin?: string; nom?: string }): string | null {
    if (donnees?.chemin) {
      return this.buildViewUrl(donnees.chemin, donnees.nom);
    }
    if (donnees?.url && !/^https?:\/\/rapports(\/|:)/i.test(donnees.url)) {
      return donnees.url;
    }
    return null;
  }

  private async ensureSignalRForRapport(): Promise<void> {
    await this.notificationService.ensureSignalRReadyBeforeRapport();
  }

  public async ProduireRapportCommande(requete: ParametresRapportCommande): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<ReponseProduireRapport>>(this.commandeUrl, JSON.stringify(requete), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportCommandeParDepartement(requete: ParametresRapportCommande): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<ReponseProduireRapport>>(this.commandeParDepartementUrl, JSON.stringify(requete), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportPreCommande(id: number, contexteApprobation: boolean = false): Promise<boolean> {
    await this.ensureSignalRForRapport();
    const url = construireUrlRapportPreCommande(this.precommandeUrl, id, contexteApprobation);
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(url).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportItem(requete: RequeteProduireRapportItem): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<ReponseProduireRapport>>(this.itemUrl, JSON.stringify(requete), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportBonCommande(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.bonCommandeUrl.replace('{id}', id.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public ObtenirPdfBonCommande(id: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.bonCommandeUrl.replace('{id}', id.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          this.http.get(`${environment.viewRapportsBaseUrl}v/${x.valeur.chemin}`, { responseType: 'blob'}).subscribe(blob => {
            resolve(blob);
          });
        } else {
          resolve(null);
        }
      });
    });
  }

  public EnvoyerRapportBonCommande(id: number, destinataires: string[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<string>>(this.bonCommandeUrl.replace('{id}', id.toString()), JSON.stringify(destinataires), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }


  public async ProduireRapportRequisition(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.requisitionUrl.replace('{id}', id.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public EnvoyerRapportRequisition(id: number, destinataires: string[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<string>>(this.requisitionUrl.replace('{id}', id.toString()), JSON.stringify(destinataires), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public ObtenirDatesRapportUtilisationEquipements(): Promise<ReponseBase<ReponseObtenirDatesRapportUtilisationEquipements>> {
    var p = this.http.get<ReponseBase<ReponseObtenirDatesRapportUtilisationEquipements>>(this.datesUtilisationEquipementUrl)
      .toPromise();

    p.catch(() => this.router.navigateByUrl('error'));

    return p;
  }

  public ObtenirRapportUtilisationEquipements(requete: ParametresRapportUtilisationEquipements): Promise<ReponseBase<ReponseRapportUtilisationEquipements>> {
    var p = this.http.post<ReponseBase<ReponseRapportUtilisationEquipements>>(this.utilisationEquipementUrl, JSON.stringify(requete), this.httpOptions)
      .toPromise();

    p.catch(() => this.router.navigateByUrl('error'));

    return p;
  }

  public ObtenirRapportEntreesSorties(requete: ParametresRapportEntreesSorties): Promise<ReponseBase<ReponseRapportEntreesSorties>> {
    var p = this.http.post<ReponseBase<ReponseRapportEntreesSorties>>(this.entresSortiesUrl, JSON.stringify(requete), this.httpOptions)
      .toPromise();

    p.catch(() => this.router.navigateByUrl('error'));

    return p;
  }

  public async ProduireRapportLocation(): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.locationUrl).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportLocationIso(): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.locationIsoUrl).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportHeuresLocation(requete: RequeteRapportHeuresLocation): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<ReponseProduireRapport>>(this.heureslocationUrl, JSON.stringify(requete), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportListeEquipement(): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.listeEquipementUrl).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportBoiteOutil(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.boiteOutilUrl.replace('{id}', id.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportSoumission(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.soumissionUrl.replace('{id}', id.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportExcel(parametres: RequeteProduireRapportExcel): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<ReponseProduireRapport>>(this.exportationExcelUrl, JSON.stringify(parametres), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportBonTravail(id: number, multiPages: boolean, indSoumission: boolean, pageVierge: boolean = false): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.bonTravailUrl.replace('{id}', id.toString()).replace('{multiPages}', multiPages.toString()).replace('{soumission}', indSoumission.toString()).replace('{pageVierge}', pageVierge.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public ObtenirPdfBonTravail(id: number, multiPages: boolean, indSoumission: boolean): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.bonTravailUrl.replace('{id}', id.toString()).replace('{multiPages}', multiPages.toString()).replace('{soumission}', indSoumission.toString()).replace('{pageVierge}', 'false')).toPromise().then(x => {
        if (x.estUnSucces) {
          this.http.get(`${environment.viewRapportsBaseUrl}v/${x.valeur.chemin}`, { responseType: 'blob'}).subscribe(blob => {
            resolve(blob);
          });
        } else {
          resolve(null);
        }
      });
    });
  }

  public EnvoyerRapportBonTravail(id: number, multiPages: boolean, indSoumission: boolean, destinataires: string[]): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http.post<ReponseBase<string>>(this.bonTravailUrl.replace('{id}', id.toString()).replace('{multiPages}', multiPages.toString()).replace('{soumission}', indSoumission.toString()), JSON.stringify(destinataires), this.httpOptions).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportAnalyseBonTravail(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.analyseBonTravailUrl.replace('{id}', id.toString()), { observe: 'response' }).toPromise().then(x => {
        if (x.ok) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportEntretienPEP(dateDebut: Date, dateFin: Date): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.entretienPEPUrl.replace('{dateDebut}', dateDebut.toISOString()).replace('{dateFin}', dateFin.toISOString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportEntretienPreventif(itemId: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.entretienPreventifUrl.replace('{itemId}', itemId.toString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportEntretienSAAQ(dateDebut: Date, dateFin: Date): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseBase<ReponseProduireRapport>>(this.entretienSAAQUrl.replace('{dateDebut}', dateDebut.toISOString()).replace('{dateFin}', dateFin.toISOString())).toPromise().then(x => {
        if (x.estUnSucces) {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportCompilationNonConformite(): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.compilationNonConformiteUrl, { observe: 'response' }).subscribe({
        next: x => {
          if (x.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: () => resolve(false)
      });
    });
  }

  public async ProduireRapportSaisieHeures(emplacement: number, departement: number, showHeures: boolean, showKms: boolean): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      let httpParams = new HttpParams();
      if (emplacement) {
        httpParams = httpParams.set('emplacement', emplacement.toString());
      }
      if (departement) {
        httpParams = httpParams.set('departement', departement.toString());
      }
      if (showHeures) {
        httpParams = httpParams.set('showHeures', 'true');
      }
      if (showKms) {
        httpParams = httpParams.set('showKms', 'true');
      }
      this.http.get<ReponseProduireRapport>(this.saisieHeuresUrl, { observe: 'response', params: httpParams }).subscribe({
        next: x => {
          if (x.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: () => resolve(false)
      });
    });
  }

  public async ProduireRapportFichesSignaletiques(emplacementId: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.fichesSignaletiquesUrl.replace('{emplacementId}', emplacementId.toString()), { observe: 'response' }).subscribe({
        next: x => {
          if (x.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (x) => {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: x.error, life: 10000 });
          resolve(false);
        }
      });
    });
  }

  public async obtenirFichesSignaletiquesPdf(emplacementId: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.fichesSignaletiquesPdfUrl.replace('{emplacementId}', emplacementId.toString()), { observe: 'response' }).subscribe({
        next: x => {
          if (x.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (x) => {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: x.error, life: 10000 });
          resolve(false);
        }
      });
    });
  }

  public async ProduireRapportSST(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.sstUrl.replace('{id}', id.toString()), { observe: 'response' }).subscribe({
        next: x => {
          if (x.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (x) => {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: x.error, life: 10000 });
          resolve(false);
        }
      });
    });
  }

  public ObtenirPdfRapportSST(id: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.sstUrl.replace('{id}', id.toString()), { observe: 'response' }).pipe(
        map(x => {
          return lastValueFrom(this.http.get(`${environment.viewRapportsBaseUrl}v/${x.body.chemin}`, { responseType: 'blob'}));
        })
      ).subscribe({
        next: x => resolve(x),
        error: error => reject(error)
      });
    });
  }

  public async ProduireRapportNonConformite(id: number): Promise<boolean> {
    await this.ensureSignalRForRapport();
    return new Promise((resolve, reject) => {
      this.http.get<ReponseProduireRapport>(this.nonConformiteUrl.replace('{id}', id.toString()), { observe: 'response' }).subscribe({
        next: x => {
          if (x.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: (x) => {
          this.messageService.add({ severity: 'error', summary: 'Erreur', detail: x.error, life: 10000 });
          resolve(false);
        }
      });
    });
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);

      this.log(`${operation} failed: ${error.message}`);

      this.router.navigateByUrl('erreurtechnique');

      return of(result as T);
    };
  }

  private log(message: string) {
    console.log(message);
  }
}
